import { useCallback, useState } from "react";

import { Button, Flex, Grid, Heading, TextField, Text } from "@radix-ui/themes";
import { useForm, ValidateResult } from "react-hook-form";

import { ShippingDetails } from "../../api/public";
import { AddressInput } from "../../common/components";

type Props = {
  defaultValue?: ShippingDetails;
  onSubmit?: (value: ShippingDetails) => void;
  onCancel?: () => void;
};

export const AddressForm = ({ defaultValue, onCancel, onSubmit }: Props) => {
  const [value, setValue] = useState<ShippingDetails | undefined>(defaultValue);
  const {
    watch,
    setError,
    clearErrors,
    trigger,
    register,
    getValues,
    formState: { errors },
  } = useForm({
    values: value,
    mode: "onSubmit",
  });

  const validateShippingDetails = useCallback(
    (shippingDetails?: ShippingDetails): ValidateResult => {
      let isValid = true;
      if (!shippingDetails?.address_line_1) {
        setError("address_line_1", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (!shippingDetails?.city) {
        setError("city", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (!shippingDetails?.country) {
        setError("country", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (!shippingDetails?.zip) {
        setError("zip", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (!shippingDetails?.state) {
        setError("state", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (isValid) {
        clearErrors();
      }
      return isValid;
    },
    [clearErrors, setError]
  );

  const handleSubmit = useCallback(() => {
    trigger(undefined, {
      shouldFocus: true,
    }).then((isValid) => {
      if (validateShippingDetails(value) && isValid) {
        onSubmit?.(getValues());
      }
    });
  }, [getValues, onSubmit, trigger, validateShippingDetails, value]);

  return (
    <Flex direction="column" gap="4">
      <AddressInput
        value={value}
        onChange={(newValue) => {
          setValue({
            ...getValues(),
            address_line_1: newValue?.address_line_1 ?? "",
            address_line_2: newValue?.address_line_2 ?? "",
            city: newValue?.city ?? "",
            state: newValue?.state ?? "",
            zip: newValue?.zip ?? "",
            country: newValue?.country ?? "",
          });
        }}
        errors={errors}
      />
      <Heading size="3">Carrier Information</Heading>
      <Grid columns="auto 1fr" gap="2" align="center">
        <Text size="1" color="gray" as="label" weight="medium">
          FedEx #
        </Text>
        <TextField.Root type="text" {...register("fedex_account_number")} />
        <Text size="1" color="gray" as="label" weight="medium">
          UPS #
        </Text>
        <TextField.Root type="text" {...register("ups_account_number")} />
        <Text size="1" color="gray" as="label" weight="medium">
          DHL #
        </Text>
        <TextField.Root type="text" {...register("dhl_account_number")} />
      </Grid>
      <Flex gap="2" justify="end">
        <Button variant="surface" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>Submit</Button>
      </Flex>
    </Flex>
  );
};
